export const UNIT_SYSTEMS = {
  IMPERIAL: 'imperial',
  METRIC: 'metric'
};

export const UNITS = {
  [UNIT_SYSTEMS.IMPERIAL]: {
    inches: { name: 'Inches', symbol: '"', factor: 1 },
    feet: { name: 'Feet', symbol: "'", factor: 12 }
  },
  [UNIT_SYSTEMS.METRIC]: {
    mm: { name: 'Millimeters', symbol: 'mm', factor: 1 },
    cm: { name: 'Centimeters', symbol: 'cm', factor: 10 },
    m: { name: 'Meters', symbol: 'm', factor: 1000 }
  }
};

const INCH_TO_MM = 25.4;

export const getDefaultUnits = () => {
  const locale = navigator.language || 'en-US';
  const isUS = locale.includes('US') || locale.includes('LR') || locale.includes('MM');
  
  return {
    system: isUS ? UNIT_SYSTEMS.IMPERIAL : UNIT_SYSTEMS.METRIC,
    unit: isUS ? 'inches' : 'cm'
  };
};

export const convertToInches = (value, fromUnit, system) => {
  if (system === UNIT_SYSTEMS.IMPERIAL) {
    const unit = UNITS[system][fromUnit];
    return value * unit.factor;
  } else {
    const unit = UNITS[system][fromUnit];
    const mm = value * unit.factor;
    return mm / INCH_TO_MM;
  }
};

export const convertFromInches = (inches, toUnit, system) => {
  if (system === UNIT_SYSTEMS.IMPERIAL) {
    const unit = UNITS[system][toUnit];
    return inches / unit.factor;
  } else {
    const mm = inches * INCH_TO_MM;
    const unit = UNITS[system][toUnit];
    return mm / unit.factor;
  }
};

export const formatValue = (value, unit, system, decimals = 2) => {
  const unitInfo = UNITS[system][unit];
  const formatted = value.toFixed(decimals).replace(/\.?0+$/, '');
  return `${formatted}${unitInfo.symbol}`;
};

/**
 * Format a dimension value with appropriate precision based on unit system.
 * Imperial: up to 3 decimals, strips trailing zeros
 * Metric mm: 1 decimal
 * Metric cm/m: 2 decimals, strips trailing zeros
 * 
 * Options:
 *   - includeUnit: append unit symbol (default true)
 *   - forceDecimals: override decimal places
 *   - snap: snap to clean integers when very close (default false, use for sheets only)
 */
export const formatDimension = (value, unit, system, options = {}) => {
  const { includeUnit = true, forceDecimals = null, snap = false } = options;
  const unitInfo = UNITS[system]?.[unit];
  
  // Snap to integer if very close (only for sheet dimensions)
  if (snap) {
    const rounded = Math.round(value);
    if (Math.abs(value - rounded) < 0.01) {
      value = rounded;
    }
  }
  
  // Determine decimal places based on unit
  let decimals;
  if (forceDecimals !== null) {
    decimals = forceDecimals;
  } else if (system === UNIT_SYSTEMS.IMPERIAL) {
    // Imperial: up to 3 decimals, strip trailing zeros
    decimals = 3;
  } else if (unit === 'mm') {
    // Metric mm: 1 decimal
    decimals = 1;
  } else {
    // Metric cm/m: 2 decimals
    decimals = 2;
  }
  
  // Format with appropriate precision
  let formatted = value.toFixed(decimals);
  
  // Strip trailing zeros and unnecessary decimal point
  formatted = formatted.replace(/\.?0+$/, '');
  
  if (includeUnit && unitInfo) {
    return `${formatted}${unitInfo.symbol}`;
  }
  return formatted;
};

export const parseValue = (input, unit, system) => {
  const unitInfo = UNITS[system][unit];
  const cleanInput = input.toString().replace(unitInfo.symbol, '').trim();
  const value = parseFloat(cleanInput);
  return isNaN(value) ? 0 : value;
};

/**
 * Parse a math expression string into a number.
 * Supports:
 *   - Plain numbers: "12", "3.5"
 *   - Fractions: "9/16", "3/4"
 *   - Mixed numbers: "1 3/4" → 1.75
 *   - Addition of fractions: "12+3/8" or "12 + 3/8" → 12.375
 *   - Simple addition/subtraction: "12+3", "10-2.5"
 * Returns NaN if the input can't be parsed.
 */
export const parseMathExpr = (input) => {
  if (input == null) return NaN;
  const s = input.toString().trim();
  if (s === '') return NaN;

  // Try plain number first (fast path)
  const plain = Number(s);
  if (!isNaN(plain) && !/[\/\+\-\s]/.test(s.slice(1))) return plain;

  // Try single fraction: "9/16"
  const fractionOnly = /^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/.exec(s);
  if (fractionOnly) {
    const num = parseFloat(fractionOnly[1]);
    const den = parseFloat(fractionOnly[2]);
    return den !== 0 ? num / den : NaN;
  }

  // Try mixed number: "1 3/4" (whole space fraction, no operator)
  const mixed = /^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/.exec(s);
  if (mixed) {
    const whole = parseFloat(mixed[1]);
    const num = parseFloat(mixed[2]);
    const den = parseFloat(mixed[3]);
    return den !== 0 ? whole + num / den : NaN;
  }

  // Try addition/subtraction with possible fractions: "12+3/8", "10-1/2", "12 + 3"
  // Split on + or - (but not a leading negative)
  const tokens = s.match(/^(-?\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)?)\s*([+\-])\s*(\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)?)$/);
  if (tokens) {
    const left = parseMathExpr(tokens[1]);
    const right = parseMathExpr(tokens[3]);
    if (!isNaN(left) && !isNaN(right)) {
      return tokens[2] === '+' ? left + right : left - right;
    }
  }

  // Fallback to parseFloat for things like "12.5in"
  const fallback = parseFloat(s);
  return isNaN(fallback) ? NaN : fallback;
};

/**
 * Format a dimension value at high precision for tooltips.
 * For imperial: snaps to nearest 1/128" (0.0078125") to eliminate floating point errors
 * from unit conversions while preserving all common fractional inch values.
 * For metric: shows 6 decimal places directly.
 * Trailing zeros are stripped (e.g., 12.125 instead of 12.125000).
 */
/**
 * Return a short unit label for CSV headers (mm, cm, m, in, ft).
 */
export const getCSVUnitLabel = (unit, system) => {
  if (system === UNIT_SYSTEMS.IMPERIAL) {
    return unit === 'feet' ? 'ft' : 'in';
  }
  return unit; // mm, cm, m
};

/**
 * Round a converted value to eliminate floating-point artifacts in CSV export.
 * mm → 1 decimal, cm → 2 decimals, in/ft/m → 4 decimals
 */
export const roundForCSV = (value, unit, system) => {
  let decimals;
  if (system === UNIT_SYSTEMS.METRIC && unit === 'mm') {
    decimals = 1;
  } else if (system === UNIT_SYSTEMS.METRIC && unit === 'cm') {
    decimals = 2;
  } else {
    decimals = 4;
  }
  return parseFloat(value.toFixed(decimals));
};

/**
 * Get sensible defaults for sheet, piece, kerf, and thickness based on current unit.
 */
export const getUnitDefaults = (unit, system) => {
  if (system === UNIT_SYSTEMS.IMPERIAL) {
    if (unit === 'feet') {
      return { sheetW: 4, sheetL: 8, kerf: 0.0104, thickness: 0.0625, pieceW: 0.67, pieceL: 1 };
    }
    // inches
    return { sheetW: 48, sheetL: 96, kerf: 0.125, thickness: 0.75, pieceW: 8, pieceL: 12 };
  }
  // metric
  if (unit === 'mm') return { sheetW: 1220, sheetL: 2440, kerf: 3.175, thickness: 18, pieceW: 200, pieceL: 300 };
  if (unit === 'cm') return { sheetW: 122, sheetL: 244, kerf: 0.3175, thickness: 1.8, pieceW: 20, pieceL: 30 };
  if (unit === 'm') return { sheetW: 1.22, sheetL: 2.44, kerf: 0.003175, thickness: 0.018, pieceW: 0.2, pieceL: 0.3 };
  // fallback to cm
  return { sheetW: 122, sheetL: 244, kerf: 0.3175, thickness: 1.8, pieceW: 20, pieceL: 30 };
};

/**
 * Unit-aware tolerance for grouping pieces by thickness.
 */
export const getThicknessTolerance = (unit, system) => {
  if (system === UNIT_SYSTEMS.IMPERIAL) {
    return unit === 'feet' ? 0.003 : 0.03125; // 1/32" for inches
  }
  if (unit === 'mm') return 0.8;
  if (unit === 'cm') return 0.08;
  if (unit === 'm') return 0.0008;
  return 0.08;
};

/**
 * Convert a value between any two units (used by 3D import and CSV import).
 * Maps context unit names: inches→in, feet→ft, passthrough for mm/cm/m.
 */
export const convertBetweenUnits = (value, fromUnit, toUnit) => {
  const unitMap = { inches: 'in', feet: 'ft' };
  const from = unitMap[fromUnit] || fromUnit;
  const to = unitMap[toUnit] || toUnit;
  const toMM = { mm: 1, cm: 10, m: 1000, 'in': 25.4, ft: 304.8 };
  if (!toMM[from] || !toMM[to]) return value;
  return value * toMM[from] / toMM[to];
};

/**
 * Build a layout's thickness/dimensions label from raw data.
 * Called at optimization time and re-called when currentUnits changes.
 */
export const formatLayoutLabel = (layout, unit, system) => {
  const rawName = (layout.stockType?.name) || 'Stock';
  const name = rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase();

  const thickStr = layout.thickness > 0
    ? ` • ${formatDimension(layout.thickness, unit, system, { snap: true })}`
    : '';

  const w = formatDimension(layout.width, unit, system, { includeUnit: false, snap: true });
  const l = formatDimension(layout.length, unit, system, { includeUnit: false, snap: true });
  const unitLabel = (unit === 'in' || unit === 'inches') ? '"' : unit;

  return `${name}${thickStr} • ${w}×${l}${unitLabel}`;
};

export const formatPreciseDimension = (value, unit, system) => {
  const unitInfo = UNITS[system]?.[unit];

  let displayValue = value;
  if (system === UNIT_SYSTEMS.IMPERIAL) {
    // Snap to nearest 1/128" to eliminate floating point conversion errors
    const onetwentyeighths = Math.round(value * 128);
    displayValue = onetwentyeighths / 128;
  }

  // Format to 6 decimals, then strip trailing zeros
  const formatted = displayValue.toFixed(6).replace(/\.?0+$/, '');
  return `${formatted}${unitInfo?.symbol || ''}`;
};